import {
  type EnumeratedStringVFrame,
  type FreeStringVFrame,
  isEnumeratedEnumVFrame,
  isEnumeratedStringVFrame,
  isFixedEnumVFrame,
  isFixedStringVFrame,
  type VFrameBase,
} from '@festina/life-general-types/life'
import { notEmpty, type Optional, typeUtils } from '@festina-life/core'

import {
  type AnyFrame,
  type AnyRangeVFrame,
  isASimpleVFrame,
  isDontUseVFrame,
  isEnumeratedVFrame,
  isFixedVFrame,
  isFreeVFrame,
  isRangeVFrame,
  isRequiredInModelFrame,
} from './types'

export const isVFrame = (frame: unknown): frame is VFrameBase =>
  !!(
    frame &&
    typeof frame === 'object' &&
    '_type' in frame &&
    typeof frame._type === 'string'
  )

export const isVFrameWithDefaultValue = <T extends VFrameBase>(
  frame: Optional<T>,
): frame is T & { defaultValue: NonNullable<unknown> } =>
  !!frame &&
  'defaultValue' in frame &&
  // Needs to use notEmpty as a defaultValue can be falsy
  notEmpty(frame.defaultValue)

export const getVFrameDefaultValues = <T extends VFrameBase, U>(
  frame: Optional<T>,
  isType: (value: unknown) => value is U,
): [U, ...U[]] | null => {
  if (
    // At the time of writing, backend doesn't use defaultValues (plural) yet.
    // This is an attempt at future-proofing multi-inputs.
    typeUtils.hasField(frame, 'defaultValues') &&
    Array.isArray(frame.defaultValues)
  ) {
    const filtered = frame.defaultValues.filter(isType)
    if (typeUtils.isNonEmptyArray(filtered)) {
      return filtered
    }
  }

  if (typeUtils.hasField(frame, 'defaultValue') && isType(frame.defaultValue)) {
    // Multi-inputs expect to set arrays as their values (setting a single item would crash).
    // We've seen no instances of the backend using defaults for multi-input values, but the types allow it.
    return [frame.defaultValue]
  }

  return null
}

export const initialFormEntriesFromVBase = <T extends Record<'_type', unknown>>(
  frame: Optional<T>,
) =>
  (createInitialFormEntries(frame) ?? {}) as Optional<Record<string, unknown>>

const createInitialFormEntries = (frame: unknown): unknown => {
  // If it is an array, we don't go deeper. Elements in arrays need to be handled in their own form, and validated individually.
  if (frame && typeof frame === 'object' && !Array.isArray(frame)) {
    const wihtDefaultValues = Object.fromEntries(
      Object.entries(frame)
        .map(([key, value]) => [
          key,
          isASimpleVFrame(value)
            ? initialFormValue(value)
            : createInitialFormEntries(value),
        ])
        .filter(([, value]) => notEmpty(value)), // get rid of undefined properties
    )

    if (Object.keys(wihtDefaultValues).length > 0) {
      return wihtDefaultValues
    }

    return undefined // object had no properties set
  }

  return undefined
}

const initialFormValue = (frame: VFrameBase) => {
  // If the frame is required in the model, and has a default value
  // we set it as an initial value, so that the user won't get an backend validation error.
  if (isRequiredInModelFrame(frame) && isVFrameWithDefaultValue(frame)) {
    return (frame as FreeStringVFrame).defaultValue
  }

  return undefined
}

/**
 * Recursively check if the frame match the values. Throws if not. Does not check arrays.
 */
export const checkFrameAndValues = <T>(values: T, frame: unknown): void => {
  if (typeof values === 'object' && values) {
    if (Array.isArray(values) && !Array.isArray(frame)) {
      throw new Error('Values and frame do not match, array expected')
    }

    if (!frame || typeof frame !== 'object') {
      throw new Error('Values and frame do not match, object expected')
    }

    Object.entries(values).forEach(([key, value]) => {
      checkFrameAndValues(value, frame[key as keyof typeof frame])
    })
  }
}

/**
 * Given a VFrame, return a list of strings that will pass its validation.
 */
export const availableStringsFromVFrame = (
  frame: Optional<VFrameBase>,
): string[] => {
  if (isEnumeratedStringVFrame(frame) || isEnumeratedEnumVFrame(frame)) {
    return frame.values?.filter(notEmpty) ?? []
  } else if (isFixedStringVFrame(frame) || isFixedEnumVFrame(frame)) {
    return [frame.value].filter(notEmpty)
  } else {
    return []
  }
}

/**
 * **This function is ONLY intended to be used in the context of working with `MFrame`s!**
 *
 * Be mindful of the `VFrame` types that could be returned for the `VFrame`s provided as input,
 * and make sure they make sense in the context they are being used within the custom form too.
 *
 * This function will merge two VFrames into a single *widened* frame.
 * That is, until a single `VFrame` is selected, the returned `VFrame`
 * may include types that are actually invalid during real validation:
 *
 * - Free frames are the broadest type, meaning other types will always widen to a free frame.
 * - Range frames have the ranges extended to fit both the lowest minimum and highest maximum.
 * - Fixed & enumerated frames alike become a single enumerated frame encompassing all values.
 * - Do-not-use frames cause the frame to not be required, as the value should not be entered.
 *
 * If either frame is missing, the other is selected.
 * */
export const mergeVFrames = (
  left: Optional<VFrameBase>,
  right: Optional<VFrameBase>,
): Optional<AnyFrame> => {
  if (isFreeVFrame(left) || !right) {
    return left
  }

  if (isFreeVFrame(right) || !left) {
    return right
  }

  if (isRangeVFrame(left) && isRangeVFrame(right)) {
    if (left._type !== right._type) {
      throw new Error(
        `Merging of range frames must have the same type: ${left._type}, ${right._type}`,
      )
    }

    const merged: AnyRangeVFrame = { _type: left._type }

    if (notEmpty(left.min) && notEmpty(right.min)) {
      merged.min = left.min < right.min ? left.min : right.min
    } else {
      merged.min = left.min ?? right.min
    }

    if (notEmpty(left.max) && notEmpty(right.max)) {
      merged.max = left.max > right.max ? left.max : right.max
    } else {
      merged.max = left.max ?? right.max
    }

    merged.defaultValue = left.defaultValue ?? right.defaultValue

    return merged
  }

  if (
    (isFixedVFrame(left) || isEnumeratedVFrame(left)) &&
    (isFixedVFrame(right) || isEnumeratedVFrame(right))
  ) {
    if (
      !(
        isFixedStringVFrame(left) ||
        isEnumeratedStringVFrame(left) ||
        isEnumeratedEnumVFrame(left)
      ) ||
      !(
        isFixedStringVFrame(right) ||
        isEnumeratedStringVFrame(right) ||
        isEnumeratedEnumVFrame(right)
      )
    ) {
      throw new Error(
        `Merging of fixed and enumerated frames is only supported on strings: ${left._type}, ${right._type}`,
      )
    }

    const merged: EnumeratedStringVFrame = { _type: 'EnumeratedStringVFrame' }

    merged.values = [
      ...new Set(
        [
          ...(isFixedStringVFrame(left) ? [left.value] : (left.values ?? [])),
          ...(isFixedStringVFrame(right)
            ? [right.value]
            : (right.values ?? [])),
        ].filter(notEmpty),
      ),
    ]

    return merged
  }

  if (isDontUseVFrame(left)) {
    return isDontUseVFrame(right) ? right : { ...right, required: false }
  }
  if (isDontUseVFrame(right)) {
    return { ...left, required: false }
  }

  if (
    !isASimpleVFrame(left) &&
    !isASimpleVFrame(right) &&
    left._type &&
    left._type === right._type
  ) {
    const result: VFrameBase & Record<string, string | Optional<VFrameBase>> = {
      _type: left._type,
    }

    const allKeys = new Set([...Object.keys(left), ...Object.keys(right)])
    allKeys.delete('_type')

    for (const key of allKeys) {
      result[key] = mergeVFrames(
        (left as VFrameBase & Record<string, VFrameBase>)[key],
        (right as VFrameBase & Record<string, VFrameBase>)[key],
      )
    }

    return result
  }

  throw new Error(
    `Merging of frames not implemented for pair: ${left._type}, ${right._type}`,
  )
}
